import {
  createProject,
  addJobToProject,
  addJobsToProject,
  editProject,
  removeJobFromProject,
} from '@/api/projects'
import type { ProjectJson } from '@ironsight/types'
import { useMutation, useQueryClient } from '@tanstack/vue-query'
import { ProjectsQueryConfig } from './useProjectsQueries'

type ProjectCreate = {
  name: string
  ownerId: string | null
}

type ProjectUpdate = {
  id: string
  name: string
}

type AddJobToProject = {
  projectId: string
  jobId: number
}

type RemoveJobFromProject = {
  projectId: string
  jobNumber: number
}

type AddJobsToProject = {
  projectId: string
  jobIds: string[]
}

export const createProjectMutation = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (project: ProjectCreate) =>
      await createProject(project.name, project.ownerId),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [ProjectsQueryConfig.queryKey, 'list'],
      })
    },
  })
}

export const editProjectMutation = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (project: ProjectUpdate) => await editProject(project.id, project.name),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: [ProjectsQueryConfig.queryKey, 'list'],
      })

      // Update the Query Cache Data for this Project.
      const curr: ProjectJson | undefined = queryClient.getQueryData([
        ProjectsQueryConfig.queryKey,
        'details',
        variables.id,
      ])

      if (!curr) return

      curr.name = variables.name

      queryClient.setQueryData([ProjectsQueryConfig.queryKey, 'details', variables.id], curr)
    },
  })
}

export const addJobToProjectMutation = () =>
  useMutation({
    mutationFn: async (obj: AddJobToProject) => await addJobToProject(obj.projectId, obj.jobId),
  })

export const addJobsToProjectMutation = () =>
  useMutation({
    mutationFn: async (obj: AddJobsToProject) => await addJobsToProject(obj.projectId, obj.jobIds),
  })

export const removeJobFromProjectMutation = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (obj: RemoveJobFromProject) =>
      await removeJobFromProject(obj.projectId, obj.jobNumber),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: [ProjectsQueryConfig.queryKey, 'details', variables.projectId],
      })
    },
  })
}
